import { ChatLoadingRequestType } from 'utils/chat/chatConstants';
import { PageTypes, PageViews, RowTypes } from 'utils/chat/instructorHugoContext/constants';

const getVisibleRowCount = (pageContext) => pageContext?.visible_rows?.ids?.length || 0;

const getCriteriaCount = (context) => context?.grades?.current?.criteria?.length || 0;

const LoadingStatusSlot = {
    PRIMARY: 'primary',
    DETAIL: 'detail',
    FINAL: 'final',
};

const SHARED_CONTEXT_VARIANTS = [
    { key: 'context-fallback-alt', defaultValue: 'Looking over the details in view...' },
    { key: 'context-fallback-warm', defaultValue: 'Bringing the relevant details together...' },
    { key: 'context-fallback-deep', defaultValue: 'Getting the fuller picture...' },
    { key: 'context-fallback-gentle', defaultValue: 'Taking a moment to get oriented...' },
];

const createSharedContextVariants = ({ key, defaultValue }) => [{ key, defaultValue }, ...SHARED_CONTEXT_VARIANTS];

const withValues = (variants, values) => variants.map((variant) => ({ ...variant, values }));

const PageContextStatus = {
    key: 'page-context',
    defaultValue: 'Putting the details together...',
    slot: LoadingStatusSlot.DETAIL,
    variants: [
        { key: 'page-context', defaultValue: 'Putting the details together...' },
        { key: 'page-context-alt', defaultValue: 'Connecting the dots...' },
        { key: 'page-context-warm', defaultValue: 'Getting the full picture...' },
        { key: 'page-context-deep', defaultValue: 'Taking everything into account...' },
        { key: 'page-context-gentle', defaultValue: 'Making sense of what is in front of me...' },
    ],
};

const FinalResponseStatus = {
    key: 'preparing-response',
    defaultValue: 'Putting together something helpful...',
    slot: LoadingStatusSlot.FINAL,
    variants: [
        { key: 'preparing-response', defaultValue: 'Putting together something helpful...' },
        { key: 'preparing-response-alt', defaultValue: 'Shaping a thoughtful response...' },
        { key: 'preparing-response-warm', defaultValue: 'Almost ready with some ideas for you...' },
        { key: 'preparing-response-deep', defaultValue: 'Bringing the key ideas together...' },
        { key: 'preparing-response-gentle', defaultValue: 'Finding the best way to explain this...' },
    ],
};

const UnknownPageStatus = {
    key: 'page',
    defaultValue: 'Getting familiar with this page...',
    slot: LoadingStatusSlot.PRIMARY,
    variants: createSharedContextVariants({ key: 'page', defaultValue: 'Getting familiar with this page...' }),
};

const createStatus = ({ key, defaultValue, slot = LoadingStatusSlot.PRIMARY, variants, values }) => {
    if (!variants?.length || variants.length < 2) {
        throw new Error(`Loading status "${key}" must define at least two variants.`);
    }

    return {
        key,
        defaultValue: variants[0].defaultValue || defaultValue,
        slot,
        ...(values ? { values } : {}),
        variants: variants.map((variant) => ({ ...variant, ...(values ? { values } : {}) })),
    };
};

const addStatus = (statuses, status) => {
    const statusWithVariants = createStatus(status);
    if (statuses.some(({ key }) => key === statusWithVariants.key)) return;
    statuses.push(statusWithVariants);
};

const getVariantIndex = ({ length, offset, salt }) => {
    const mixedSalt = (salt + offset * 2654435761) >>> 0;
    return mixedSalt % length;
};

const selectStatusVariant = (status, index, salt) => {
    return status.variants[getVariantIndex({ length: status.variants.length, offset: index, salt })];
};

const getDetailStatus = (statuses) =>
    statuses.find(({ slot }) => slot === LoadingStatusSlot.DETAIL) || PageContextStatus;

const selectLoadingStatusSequence = ({ statuses, salt }) =>
    [statuses[0] || UnknownPageStatus, getDetailStatus(statuses), FinalResponseStatus].map((status, index) =>
        selectStatusVariant(status, index, salt)
    );

const LOADING_TITLE_VARIANTS = [
    { key: 'working-title', defaultValue: 'Working through this with you...' },
    { key: 'working-title-alt', defaultValue: 'Taking a thoughtful look...' },
    { key: 'working-title-warm', defaultValue: 'Putting the pieces together...' },
    { key: 'working-title-deep', defaultValue: 'Getting everything in place...' },
    { key: 'working-title-gentle', defaultValue: 'Thinking this through...' },
    { key: 'working-title-focused', defaultValue: 'Looking over the details...' },
    { key: 'working-title-context', defaultValue: 'Gathering the relevant context...' },
    { key: 'working-title-picture', defaultValue: 'Getting the full picture...' },
    { key: 'working-title-next', defaultValue: 'Working out the next step...' },
    { key: 'working-title-careful', defaultValue: 'Taking a closer look...' },
    { key: 'working-title-organized', defaultValue: 'Bringing the pieces together...' },
    { key: 'working-title-oriented', defaultValue: 'Getting oriented...' },
];

export const buildInstructorHugoLoadingTitleSpecs = () => LOADING_TITLE_VARIANTS;

export const selectInstructorHugoLoadingTitle = ({ salt = 0 }) => {
    return LOADING_TITLE_VARIANTS[getVariantIndex({ length: LOADING_TITLE_VARIANTS.length, offset: 37, salt })];
};

const VISIBLE_ROW_STATUS_BY_TYPE = {
    [RowTypes.ASSIGNMENTS]: {
        key: 'assignments-count',
        defaultValue: 'Taking in {{count}} assignments...',
        variants: [
            { key: 'assignments-count', defaultValue: 'Taking in {{count}} assignments...' },
            { key: 'assignments-count-alt', defaultValue: 'Looking across {{count}} assignments...' },
            { key: 'assignments-count-warm', defaultValue: 'Reviewing the {{count}} assignments in view...' },
            { key: 'assignments-count-deep', defaultValue: 'Getting a sense of {{count}} assignments...' },
        ],
    },
    [RowTypes.SUBMISSIONS]: {
        key: 'submissions-count',
        defaultValue: 'Taking in {{count}} submissions...',
        variants: [
            { key: 'submissions-count', defaultValue: 'Taking in {{count}} submissions...' },
            { key: 'submissions-count-alt', defaultValue: 'Looking across {{count}} submissions...' },
            { key: 'submissions-count-warm', defaultValue: 'Reviewing the {{count}} submissions in view...' },
            { key: 'submissions-count-deep', defaultValue: 'Getting a sense of {{count}} submissions...' },
        ],
    },
    [RowTypes.GRADES]: {
        key: 'grades-count',
        defaultValue: 'Taking in {{count}} grades...',
        variants: [
            { key: 'grades-count', defaultValue: 'Taking in {{count}} grades...' },
            { key: 'grades-count-alt', defaultValue: 'Looking across {{count}} grades...' },
            { key: 'grades-count-warm', defaultValue: 'Reviewing the {{count}} grades in view...' },
            { key: 'grades-count-deep', defaultValue: 'Getting a sense of {{count}} grades...' },
        ],
    },
    [RowTypes.PEOPLE]: {
        key: 'people-count',
        defaultValue: 'Getting to know {{count}} people...',
        variants: [
            { key: 'people-count', defaultValue: 'Getting to know {{count}} people...' },
            { key: 'people-count-alt', defaultValue: 'Looking over {{count}} people...' },
            { key: 'people-count-warm', defaultValue: 'Reviewing the {{count}} people in view...' },
            { key: 'people-count-deep', defaultValue: 'Getting a sense of {{count}} people...' },
        ],
    },
    [RowTypes.RUBRICS]: {
        key: 'rubrics-count',
        defaultValue: 'Taking in {{count}} rubrics...',
        variants: [
            { key: 'rubrics-count', defaultValue: 'Taking in {{count}} rubrics...' },
            { key: 'rubrics-count-alt', defaultValue: 'Looking across {{count}} rubrics...' },
            { key: 'rubrics-count-warm', defaultValue: 'Reviewing the {{count}} rubrics in view...' },
            { key: 'rubrics-count-deep', defaultValue: 'Getting a sense of {{count}} rubrics...' },
        ],
    },
    [RowTypes.COURSES]: {
        key: 'courses-count',
        defaultValue: 'Getting familiar with {{count}} courses...',
        variants: [
            { key: 'courses-count', defaultValue: 'Reviewing {{count}} courses...' },
            { key: 'courses-count-alt', defaultValue: 'Looking across {{count}} courses...' },
            { key: 'courses-count-warm', defaultValue: 'Checking the {{count}} courses in view...' },
            { key: 'courses-count-deep', defaultValue: 'Getting a sense of {{count}} courses...' },
        ],
    },
};

const getVisibleRowsStatus = ({ pageContext, statuses }) => {
    const rowCount = getVisibleRowCount(pageContext);
    if (!rowCount) return;

    const status = VISIBLE_ROW_STATUS_BY_TYPE[pageContext?.visible_rows?.type];
    if (!status) return;

    addStatus(statuses, {
        ...status,
        slot: LoadingStatusSlot.DETAIL,
        values: { count: rowCount },
        ...(status.variants
            ? {
                  variants: status.variants.map((variant) => ({ ...variant, values: { count: rowCount } })),
              }
            : {}),
    });
};

const buildContinuationStatus = ({ statuses, assignmentName }) => {
    const continuationVariants = assignmentName
        ? [
              { key: 'continuing-assignment-context', defaultValue: 'Continuing with {{assignmentName}}...' },
              {
                  key: 'continuing-assignment-context-alt',
                  defaultValue: 'Picking up where we left off on {{assignmentName}}...',
              },
              {
                  key: 'continuing-assignment-context-warm',
                  defaultValue: 'Reviewing the next part of {{assignmentName}}...',
              },
              {
                  key: 'continuing-assignment-context-deep',
                  defaultValue: 'Moving forward with {{assignmentName}}...',
              },
          ]
        : [
              { key: 'continuing-context', defaultValue: 'Picking up where we left off...' },
              { key: 'continuing-context-alt', defaultValue: 'Continuing with the next part...' },
              { key: 'continuing-context-warm', defaultValue: 'Following up on the conversation...' },
              { key: 'continuing-context-deep', defaultValue: 'Carrying this forward...' },
          ];

    addStatus(statuses, {
        key: continuationVariants[0].key,
        defaultValue: continuationVariants[0].defaultValue,
        ...(assignmentName ? { values: { assignmentName } } : {}),
        variants: withValues(continuationVariants, assignmentName ? { assignmentName } : {}),
    });
};

const buildCourseListStatus = ({ statuses, pageContext }) => {
    addStatus(statuses, {
        key: pageContext.active_tab === PageViews.ARCHIVED_COURSES ? 'archived-courses-page' : 'course-page',
        defaultValue:
            pageContext.active_tab === PageViews.ARCHIVED_COURSES
                ? 'Looking over your archived course list...'
                : 'Looking over your course dashboard...',
        variants:
            pageContext.active_tab === PageViews.ARCHIVED_COURSES
                ? [
                      {
                          key: 'archived-courses-page',
                          defaultValue: 'Looking over your archived course list...',
                      },
                      {
                          key: 'archived-courses-page-alt',
                          defaultValue: 'Checking your archived courses...',
                      },
                      {
                          key: 'archived-courses-page-warm',
                          defaultValue: 'Opening your archived course overview...',
                      },
                      {
                          key: 'archived-courses-page-deep',
                          defaultValue: 'Getting oriented with your archived courses...',
                      },
                  ]
                : [
                      { key: 'course-page', defaultValue: 'Looking over your course dashboard...' },
                      { key: 'course-page-alt', defaultValue: 'Checking your course overview...' },
                      { key: 'course-page-warm', defaultValue: 'Opening your course list...' },
                      { key: 'course-page-deep', defaultValue: 'Getting oriented with your courses...' },
                  ],
    });
};

const buildAssignmentListStatus = ({ statuses, courseName }) => {
    addStatus(statuses, {
        key: courseName ? 'assignments-course-context' : 'assignments-page',
        defaultValue: courseName ? 'Reviewing assignments in {{courseName}}...' : 'Reviewing your assignments...',
        ...(courseName
            ? {
                  values: { courseName },
                  variants: withValues(
                      [
                          {
                              key: 'assignments-course-context',
                              defaultValue: 'Reviewing assignments in {{courseName}}...',
                          },
                          {
                              key: 'assignments-course-context-alt',
                              defaultValue: 'Looking over the assignments in {{courseName}}...',
                          },
                          {
                              key: 'assignments-course-context-warm',
                              defaultValue: 'Checking what is happening in {{courseName}}...',
                          },
                          {
                              key: 'assignments-course-context-deep',
                              defaultValue: 'Getting oriented with the assignments in {{courseName}}...',
                          },
                      ],
                      { courseName }
                  ),
              }
            : {}),
        ...(!courseName
            ? {
                  variants: createSharedContextVariants({
                      key: 'assignments-page',
                      defaultValue: 'Reviewing your assignments...',
                  }),
              }
            : {}),
    });
};

const buildAssignmentCreateStatus = ({ statuses }) => {
    addStatus(statuses, {
        key: 'assignment-create',
        defaultValue: 'Getting a feel for your new assignment...',
        variants: createSharedContextVariants({
            key: 'assignment-create',
            defaultValue: 'Getting a feel for your new assignment...',
        }),
    });
};

const buildAssignmentEditStatus = ({ statuses, assignmentName }) => {
    addStatus(statuses, {
        key: assignmentName ? 'assignment-context' : 'assignment-edit',
        defaultValue: assignmentName ? 'Reviewing {{assignmentName}}...' : 'Reviewing this assignment...',
        ...(assignmentName
            ? {
                  values: { assignmentName },
                  variants: withValues(
                      [
                          { key: 'assignment-context', defaultValue: 'Reviewing {{assignmentName}}...' },
                          {
                              key: 'assignment-context-alt',
                              defaultValue: 'Looking over {{assignmentName}}...',
                          },
                          {
                              key: 'assignment-context-warm',
                              defaultValue: 'Checking the details for {{assignmentName}}...',
                          },
                          {
                              key: 'assignment-context-deep',
                              defaultValue: 'Getting oriented with {{assignmentName}}...',
                          },
                      ],
                      { assignmentName }
                  ),
              }
            : {}),
        ...(!assignmentName
            ? {
                  variants: createSharedContextVariants({
                      key: 'assignment-edit',
                      defaultValue: 'Reviewing this assignment...',
                  }),
              }
            : {}),
    });
};

const buildAssignmentOverviewStatus = ({ statuses, assignmentName }) => {
    addStatus(statuses, {
        key: assignmentName ? 'assignment-context' : 'assignment-overview',
        defaultValue: assignmentName ? 'Reviewing {{assignmentName}}...' : 'Reviewing this assignment...',
        ...(assignmentName
            ? {
                  values: { assignmentName },
                  variants: withValues(
                      [
                          { key: 'assignment-context', defaultValue: 'Reviewing {{assignmentName}}...' },
                          {
                              key: 'assignment-context-alt',
                              defaultValue: 'Looking over {{assignmentName}}...',
                          },
                          {
                              key: 'assignment-context-warm',
                              defaultValue: 'Checking the details for {{assignmentName}}...',
                          },
                          {
                              key: 'assignment-context-deep',
                              defaultValue: 'Getting oriented with {{assignmentName}}...',
                          },
                      ],
                      { assignmentName }
                  ),
              }
            : {}),
        ...(!assignmentName
            ? {
                  variants: createSharedContextVariants({
                      key: 'assignment-overview',
                      defaultValue: 'Reviewing this assignment...',
                  }),
              }
            : {}),
    });
};

const buildAssignmentPreviewStatus = ({ statuses, assignmentName }) => {
    addStatus(statuses, {
        key: assignmentName ? 'assignment-preview-context' : 'assignment-preview',
        defaultValue: assignmentName
            ? 'Reviewing the preview for {{assignmentName}}...'
            : 'Reviewing the assignment preview...',
        ...(assignmentName
            ? {
                  values: { assignmentName },
                  variants: withValues(
                      [
                          {
                              key: 'assignment-preview-context',
                              defaultValue: 'Reviewing the preview for {{assignmentName}}...',
                          },
                          {
                              key: 'assignment-preview-context-alt',
                              defaultValue: 'Looking over the preview of {{assignmentName}}...',
                          },
                          {
                              key: 'assignment-preview-context-warm',
                              defaultValue: 'Checking how {{assignmentName}} is coming together...',
                          },
                      ],
                      { assignmentName }
                  ),
              }
            : {}),
        ...(!assignmentName
            ? {
                  variants: createSharedContextVariants({
                      key: 'assignment-preview',
                      defaultValue: 'Reviewing the assignment preview...',
                  }),
              }
            : {}),
    });
};

const buildGradingStatus = ({ statuses, assignmentName, hasSelectedItem }) => {
    addStatus(statuses, {
        key: assignmentName
            ? hasSelectedItem
                ? 'grading-assignment-context-selected'
                : 'grading-assignment-context'
            : hasSelectedItem
              ? 'selected-grading-page'
              : 'grading-page',
        defaultValue: assignmentName
            ? hasSelectedItem
                ? "Reviewing {{assignmentName}} and this student's work..."
                : 'Reviewing grading for {{assignmentName}}...'
            : hasSelectedItem
              ? "Reviewing this student's work..."
              : 'Reviewing the grading details...',
        ...(assignmentName
            ? {
                  values: { assignmentName },
                  variants: withValues(
                      [
                          {
                              key: hasSelectedItem
                                  ? 'grading-assignment-context-selected'
                                  : 'grading-assignment-context',
                              defaultValue: hasSelectedItem
                                  ? "Reviewing {{assignmentName}} and this student's work..."
                                  : 'Reviewing grading for {{assignmentName}}...',
                          },
                          {
                              key: hasSelectedItem
                                  ? 'grading-assignment-context-selected-alt'
                                  : 'grading-assignment-context-alt',
                              defaultValue: hasSelectedItem
                                  ? "Looking over {{assignmentName}} and this student's work..."
                                  : 'Checking grading details for {{assignmentName}}...',
                          },
                          {
                              key: hasSelectedItem
                                  ? 'grading-assignment-context-selected-warm'
                                  : 'grading-assignment-context-warm',
                              defaultValue: hasSelectedItem
                                  ? "Reviewing this student's work for {{assignmentName}}..."
                                  : 'Looking at the grading for {{assignmentName}}...',
                          },
                      ],
                      { assignmentName }
                  ),
              }
            : {
                  variants: createSharedContextVariants({
                      key: hasSelectedItem ? 'selected-grading-page' : 'grading-page',
                      defaultValue: hasSelectedItem
                          ? "Reviewing this student's work..."
                          : 'Reviewing the grading details...',
                  }),
              }),
    });
};

const buildSubmissionListStatus = ({ statuses, pageContext, scope, assignmentName }) => {
    addStatus(statuses, {
        key:
            pageContext.active_tab === PageViews.GRADING_INSIGHTS
                ? 'grading-insights-page'
                : scope.assignment_id && assignmentName
                  ? 'submissions-assignment-context'
                  : scope.assignment_id
                    ? 'submissions-page-assignment'
                    : 'submissions-page',
        defaultValue:
            pageContext.active_tab === PageViews.GRADING_INSIGHTS
                ? 'Reviewing patterns in the grading...'
                : scope.assignment_id && assignmentName
                  ? 'Reviewing submissions for {{assignmentName}}...'
                  : scope.assignment_id
                    ? 'Reviewing the submissions for this assignment...'
                    : 'Reviewing the submissions...',
        ...(pageContext.active_tab === PageViews.GRADING_INSIGHTS
            ? {
                  variants: createSharedContextVariants({
                      key: 'grading-insights-page',
                      defaultValue: 'Reviewing patterns in the grading...',
                  }),
              }
            : {}),
        ...(!assignmentName && scope.assignment_id
            ? {
                  variants: [
                      {
                          key: 'submissions-page-assignment',
                          defaultValue: 'Reviewing the submissions for this assignment...',
                      },
                      {
                          key: 'submissions-page-assignment-alt',
                          defaultValue: "Looking through this assignment's submissions...",
                      },
                      {
                          key: 'submissions-page-assignment-warm',
                          defaultValue: 'Checking the work submitted for this assignment...',
                      },
                      {
                          key: 'submissions-page-assignment-deep',
                          defaultValue: "Getting a sense of this assignment's submissions...",
                      },
                  ],
              }
            : {}),
        ...(!assignmentName && !scope.assignment_id && pageContext.active_tab !== PageViews.GRADING_INSIGHTS
            ? {
                  variants: [
                      { key: 'submissions-page', defaultValue: 'Reviewing the submissions...' },
                      { key: 'submissions-page-alt', defaultValue: 'Looking through the submissions...' },
                      { key: 'submissions-page-warm', defaultValue: 'Checking the submitted work...' },
                      {
                          key: 'submissions-page-deep',
                          defaultValue: 'Getting a sense of the submissions...',
                      },
                  ],
              }
            : {}),
        ...(assignmentName && pageContext.active_tab !== PageViews.GRADING_INSIGHTS
            ? {
                  values: { assignmentName },
                  variants: withValues(
                      [
                          {
                              key: 'submissions-assignment-context',
                              defaultValue: 'Reviewing submissions for {{assignmentName}}...',
                          },
                          {
                              key: 'submissions-assignment-context-alt',
                              defaultValue: 'Looking across the submissions for {{assignmentName}}...',
                          },
                          {
                              key: 'submissions-assignment-context-warm',
                              defaultValue: 'Checking the submitted work for {{assignmentName}}...',
                          },
                      ],
                      { assignmentName }
                  ),
              }
            : {}),
    });
};

const buildSubmissionDetailStatus = ({ statuses, assignmentName }) => {
    addStatus(statuses, {
        key: assignmentName ? 'submission-assignment-context' : 'submission-detail',
        defaultValue: assignmentName
            ? 'Reviewing a submission for {{assignmentName}}...'
            : 'Reviewing this submission...',
        ...(assignmentName
            ? {
                  values: { assignmentName },
                  variants: withValues(
                      [
                          {
                              key: 'submission-assignment-context',
                              defaultValue: 'Reviewing a submission for {{assignmentName}}...',
                          },
                          {
                              key: 'submission-assignment-context-alt',
                              defaultValue: 'Looking over work submitted for {{assignmentName}}...',
                          },
                          {
                              key: 'submission-assignment-context-warm',
                              defaultValue: 'Checking this submission against {{assignmentName}}...',
                          },
                      ],
                      { assignmentName }
                  ),
              }
            : {}),
        ...(!assignmentName
            ? {
                  variants: createSharedContextVariants({
                      key: 'submission-detail',
                      defaultValue: 'Reviewing this submission...',
                  }),
              }
            : {}),
    });
};

const buildPeopleListStatus = ({ statuses }) => {
    addStatus(statuses, {
        key: 'people-page',
        defaultValue: 'Getting to know the people in this course...',
        variants: [
            { key: 'people-page', defaultValue: 'Getting to know the people in this course...' },
            { key: 'people-page-alt', defaultValue: 'Looking over the people in this course...' },
            { key: 'people-page-warm', defaultValue: "Reviewing who's in this course..." },
            { key: 'people-page-deep', defaultValue: 'Getting a sense of the people here...' },
            {
                key: 'people-page-gentle',
                defaultValue: 'Taking in the people and roles in this course...',
            },
        ],
    });
};

const buildRubricListStatus = ({ statuses }) => {
    addStatus(statuses, {
        key: 'rubrics-page',
        defaultValue: 'Looking through your rubrics...',
        variants: createSharedContextVariants({
            key: 'rubrics-page',
            defaultValue: 'Looking through your rubrics...',
        }),
    });
};

const buildRubricCreateStatus = ({ statuses }) => {
    addStatus(statuses, {
        key: 'rubric-create',
        defaultValue: 'Getting a feel for your new rubric...',
        variants: createSharedContextVariants({
            key: 'rubric-create',
            defaultValue: 'Getting a feel for your new rubric...',
        }),
    });
};

const buildRubricOverviewStatus = ({ statuses, rubricName }) => {
    addStatus(statuses, {
        key: rubricName ? 'rubric-context' : 'rubric-overview',
        defaultValue: rubricName ? 'Reviewing {{rubricName}}...' : 'Reviewing this rubric...',
        ...(rubricName
            ? {
                  values: { rubricName },
                  variants: withValues(
                      [
                          { key: 'rubric-context', defaultValue: 'Reviewing {{rubricName}}...' },
                          { key: 'rubric-context-alt', defaultValue: 'Looking over {{rubricName}}...' },
                          {
                              key: 'rubric-context-warm',
                              defaultValue: 'Checking the details of {{rubricName}}...',
                          },
                      ],
                      { rubricName }
                  ),
              }
            : {}),
        ...(!rubricName
            ? {
                  variants: createSharedContextVariants({
                      key: 'rubric-overview',
                      defaultValue: 'Reviewing this rubric...',
                  }),
              }
            : {}),
    });
};

const buildRubricEditStatus = ({ statuses, rubricName }) => {
    addStatus(statuses, {
        key: rubricName ? 'rubric-context' : 'rubric-edit',
        defaultValue: rubricName ? 'Reviewing {{rubricName}}...' : 'Reviewing this rubric...',
        ...(rubricName
            ? {
                  values: { rubricName },
                  variants: withValues(
                      [
                          { key: 'rubric-context', defaultValue: 'Reviewing {{rubricName}}...' },
                          { key: 'rubric-context-alt', defaultValue: 'Looking over {{rubricName}}...' },
                          {
                              key: 'rubric-context-warm',
                              defaultValue: 'Checking the details of {{rubricName}}...',
                          },
                      ],
                      { rubricName }
                  ),
              }
            : {}),
        ...(!rubricName
            ? {
                  variants: createSharedContextVariants({
                      key: 'rubric-edit',
                      defaultValue: 'Reviewing this rubric...',
                  }),
              }
            : {}),
    });
};

const buildUnknownPageStatus = ({ statuses }) => {
    addStatus(statuses, {
        key: 'page',
        defaultValue: 'Getting familiar with this page...',
        variants: createSharedContextVariants({
            key: 'page',
            defaultValue: 'Getting familiar with this page...',
        }),
    });
};

const PAGE_STATUS_BUILDERS = {
    [PageTypes.COURSE_LIST]: buildCourseListStatus,
    [PageTypes.ASSIGNMENT_LIST]: buildAssignmentListStatus,
    [PageTypes.ASSIGNMENT_CREATE]: buildAssignmentCreateStatus,
    [PageTypes.ASSIGNMENT_EDIT]: buildAssignmentEditStatus,
    [PageTypes.ASSIGNMENT_OVERVIEW]: buildAssignmentOverviewStatus,
    [PageTypes.ASSIGNMENT_PREVIEW]: buildAssignmentPreviewStatus,
    [PageTypes.GRADING]: buildGradingStatus,
    [PageTypes.SUBMISSION_LIST]: buildSubmissionListStatus,
    [PageTypes.SUBMISSION_DETAIL]: buildSubmissionDetailStatus,
    [PageTypes.PEOPLE_LIST]: buildPeopleListStatus,
    [PageTypes.RUBRIC_LIST]: buildRubricListStatus,
    [PageTypes.RUBRIC_CREATE]: buildRubricCreateStatus,
    [PageTypes.RUBRIC_OVERVIEW]: buildRubricOverviewStatus,
    [PageTypes.RUBRIC_EDIT]: buildRubricEditStatus,
};

const buildPageStatus = ({
    statuses,
    requestType,
    pageType,
    pageContext,
    scope,
    assignmentName,
    courseName,
    rubricName,
    hasSelectedItem,
}) => {
    if (requestType === ChatLoadingRequestType.CONTINUATION) {
        buildContinuationStatus({ statuses, assignmentName });
        return;
    }

    const pageBuilder = PAGE_STATUS_BUILDERS[pageType] || buildUnknownPageStatus;
    pageBuilder({ statuses, pageContext, scope, assignmentName, courseName, rubricName, hasSelectedItem });
};

const buildCriteriaStatus = ({ context, pageContext, statuses }) => {
    const criteriaCount = getCriteriaCount(context);
    if (!criteriaCount) return;
    const visibleRowType = pageContext?.visible_rows?.type;
    const visibleRowCount = getVisibleRowCount(pageContext);
    const combinedCountKey =
        visibleRowCount && visibleRowType === RowTypes.GRADES
            ? 'grades-criteria-context'
            : visibleRowCount && visibleRowType === RowTypes.SUBMISSIONS
              ? 'submissions-criteria-context'
              : null;

    addStatus(statuses, {
        key: combinedCountKey || 'criteria-count',
        slot: LoadingStatusSlot.DETAIL,
        defaultValue: combinedCountKey
            ? 'Reviewing {{count}} items across {{criteriaCount}} {{criteriaLabel}}...'
            : 'Reviewing {{count}} rubric criteria...',
        values: combinedCountKey
            ? {
                  count: visibleRowCount,
                  criteriaCount,
                  criteriaLabel: criteriaCount === 1 ? 'rubric criterion' : 'rubric criteria',
              }
            : { count: criteriaCount },
        ...(combinedCountKey
            ? {
                  variants: withValues(
                      [
                          {
                              key: combinedCountKey,
                              defaultValue: 'Reviewing {{count}} items across {{criteriaCount}} {{criteriaLabel}}...',
                          },
                          {
                              key: `${combinedCountKey}-alt`,
                              defaultValue: 'Comparing {{count}} items with {{criteriaCount}} {{criteriaLabel}}...',
                          },
                          {
                              key: `${combinedCountKey}-warm`,
                              defaultValue:
                                  'Connecting the {{criteriaCount}} {{criteriaLabel}} to these {{count}} items...',
                          },
                      ],
                      {
                          count: visibleRowCount,
                          criteriaCount,
                          criteriaLabel: criteriaCount === 1 ? 'rubric criterion' : 'rubric criteria',
                      }
                  ),
              }
            : {
                  variants: createSharedContextVariants({
                      key: 'criteria-count',
                      defaultValue: 'Reviewing {{count}} rubric criteria...',
                  }),
              }),
    });
};

const buildUnsavedGradingStatus = ({ context, statuses }) => {
    if (!context?.grades?.current?.is_unsaved) return;
    addStatus(statuses, {
        key: 'unsaved-grading',
        defaultValue: 'Keeping your latest grading updates in mind...',
        slot: LoadingStatusSlot.DETAIL,
        variants: createSharedContextVariants({
            key: 'unsaved-grading',
            defaultValue: 'Keeping your latest grading updates in mind...',
        }),
    });
};

export const buildInstructorHugoLoadingStatusSpecs = ({
    context = null,
    pageContext = {},
    requestType = null,
    scope = {},
    salt = 0,
}) => {
    const statuses = [];
    const pageType = pageContext.page_type;
    const hasSelectedItem = Boolean(pageContext.selected_item_id);
    const assignmentName = pageContext.assignment_name || context?.assignment_name;
    const courseName = pageContext.course_name || context?.course_name;
    const rubricName = pageContext.rubric_name || context?.rubric_name;
    buildPageStatus({
        statuses,
        requestType,
        pageType,
        pageContext,
        scope,
        assignmentName,
        courseName,
        rubricName,
        hasSelectedItem,
    });

    getVisibleRowsStatus({ pageContext, statuses });

    buildCriteriaStatus({ context, pageContext, statuses });
    buildUnsavedGradingStatus({ context, statuses });

    addStatus(statuses, PageContextStatus);

    return selectLoadingStatusSequence({ statuses, salt });
};
